"use client"

import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export function RibbonToolbar({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex w-fit max-w-full items-center gap-1.5 overflow-x-auto rounded-[10px] border border-[var(--ed-ribbon-group-divider)] bg-[var(--ed-ribbon-popup-bg)] px-2 py-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]",
        className,
      )}
    >
      {children}
    </div>
  )
}

export function RibbonGroup({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-0.5 rounded-md bg-[var(--ed-ribbon-group-bg)] px-1 py-0.5",
        className,
      )}
    >
      {children}
    </div>
  )
}

export function RibbonDivider({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "mx-0.5 h-6 w-px shrink-0 bg-[var(--ed-ribbon-group-divider)]",
        className,
      )}
      aria-hidden
    />
  )
}

type RibbonIconButtonProps = {
  icon: string
  hoverHint?: string
  label: string
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  className?: string
  iconClassName?: string
  children?: React.ReactNode
}

export function RibbonIconButton({
  icon,
  hoverHint,
  label,
  onClick,
  active,
  disabled,
  className,
  iconClassName,
  children,
}: RibbonIconButtonProps) {
  const button = (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      // Keep Monaco selection/cursor when clicking the ribbon (don't steal focus)
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px]",
        "text-[#4B5563] transition-colors",
        "hover:bg-[var(--ed-ribbon-group-active-bg)] hover:shadow-sm",
        "disabled:pointer-events-none disabled:opacity-40",
        active && "bg-[var(--ed-ribbon-group-active-bg)] shadow-sm",
        className,
      )}
    >
      <img
        src={icon}
        alt=""
        draggable={false}
        className={cn("h-4 w-4 object-contain", iconClassName)}
      />
      {children}
    </button>
  )

  if (!hoverHint) return button

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={6}
        hideArrow
        className="border-0 bg-transparent p-0 shadow-none"
      >
        <img
          src={hoverHint}
          alt={label}
          draggable={false}
          className="max-w-none drop-shadow-lg"
        />
      </TooltipContent>
    </Tooltip>
  )
}

export function RibbonDropdownTrigger({
  icon,
  hoverHint,
  label,
  onClick,
  className,
}: {
  icon: string
  hoverHint?: string
  label: string
  onClick?: () => void
  className?: string
}) {
  return (
    <RibbonIconButton
      icon={icon}
      hoverHint={hoverHint}
      label={label}
      onClick={onClick}
      className={cn("w-auto gap-0.5 px-1.5", className)}
    >
      <svg
        width="8"
        height="8"
        viewBox="0 0 8 8"
        fill="none"
        className="ml-0.5 opacity-60"
        aria-hidden
      >
        <path d="M1.5 3L4 5.5L6.5 3" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    </RibbonIconButton>
  )
}
